import { Injectable } from '@angular/core';
import * as _ from 'lodash';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { BaseAPIService } from './base-api.service';
import IGridicsUseTag = ZoneIQCalibration.IGridicsUseTag;
import { GridicsUseTag } from 'src/app/interfaces/gridics-use-tag.interface';
import { gridicsUseTagMapperFactory } from 'src/app/mappers/gridics-use-tag.mapper';

const API = environment.ZONEIQ_CALIBRATION + '/v1/gridics-use-tags';

@Injectable({
    providedIn: 'root'
})
export class GridicsUseTagService extends BaseAPIService {
    gridicsUseTags: BehaviorSubject<IGridicsUseTag[]> = new BehaviorSubject<IGridicsUseTag[]>([]);

    private readonly mapper = gridicsUseTagMapperFactory();

    constructor(private readonly http: HttpClient) {
        super();
    }

    private sortUseTags(useTags: IGridicsUseTag[]): IGridicsUseTag[] {
        return [...useTags].sort((left, right) =>
            (left?.GridicsUse || '').localeCompare(right?.GridicsUse || '', undefined, { sensitivity: 'base' })
        );
    }

    getAll() {
        return this.http.get<Response>(API).pipe(
            tap(response => {
                const useTags = this.getData<IGridicsUseTag[]>(response, [])
                    .map(useTagDTO => this.mapper.fromDTO(useTagDTO))
                    .filter(useTag => useTag);
                this.gridicsUseTags.next(this.sortUseTags(useTags));
            })
        );
    }

    createGridicsUseTag(useTag: IGridicsUseTag): Observable<GridicsUseTag> {
        if (!useTag) {
            return of();
        }

        const payload = _.pick(this.mapper.toDTO(useTag), ['GridicsUse']);

        return this.http.post<Response>(API, payload).pipe(
            map(response => {
                const useTagDTO = this.getData<IGridicsUseTag>(response);
                return useTagDTO ? this.mapper.fromDTO(useTagDTO) : null;
            }),
            tap(createdUseTag => {
                if (!createdUseTag) {
                    return;
                }

                this.gridicsUseTags.next(this.sortUseTags([...this.gridicsUseTags.value, createdUseTag]));
                return createdUseTag;
            })
        );
    }

    updateGridicsUseTag(useTag: IGridicsUseTag): Observable<GridicsUseTag> {
        if (!_.get(useTag, 'Id')) {
            return of();
        }

        const payload = _.pick(this.mapper.toDTO(useTag), ['GridicsUse']);

        return this.http.post<Response>(`${API}/${useTag.Id}`, payload).pipe(
            map(response => {
                const useTagDTO = this.getData<IGridicsUseTag>(response);
                return useTagDTO ? this.mapper.fromDTO(useTagDTO) : null;
            }),
            tap(updatedUseTag => {
                if (!updatedUseTag) {
                    return;
                }

                const useTags = [...this.gridicsUseTags.value];
                const index = useTags.findIndex(existingUseTag => existingUseTag.Id === updatedUseTag.Id);

                if (index >= 0) {
                    useTags[index] = updatedUseTag;
                } else {
                    useTags.push(updatedUseTag);
                }

                this.gridicsUseTags.next(this.sortUseTags(useTags));
                return updatedUseTag;
            })
        );
    }

    deleteGridicsUseTag(useTagId: number): Observable<GridicsUseTag> {
        if (!useTagId) {
            return;
        }

        return this.http.delete<Response>(`${API}/${useTagId}`).pipe(
            map(response => {
                const useTagDTO = this.getData<IGridicsUseTag>(response);
                return useTagDTO ? this.mapper.fromDTO(useTagDTO) : null;
            }),
            tap(deletedUseTag => {
                if (!deletedUseTag) {
                    return;
                }

                this.gridicsUseTags.next(
                    this.gridicsUseTags.value.filter(cachedUseTag => cachedUseTag.Id !== deletedUseTag.Id)
                );
                return deletedUseTag;
            })
        );
    }
}
